import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Progress } from '../ui/progress';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../lib/auth-context';
import api from '../../lib/api';
import {
  Zap,
  Droplets,
  Flame,
  Leaf,
  Award,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Target,
  RefreshCw,
  Plus,
  Activity,
  Gauge
} from 'lucide-react';

export default function EnergyPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [meters, setMeters] = useState([]);
  const [goals, setGoals] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMeterDialog, setShowMeterDialog] = useState(false);
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const { toast } = useToast();
  const { token } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, metersRes, goalsRes, certsRes, alertsRes] = await Promise.all([
        api.get('/api/energy/dashboard'),
        api.get('/api/energy/meters'),
        api.get('/api/energy/goals'),
        api.get('/api/energy/certifications'),
        api.get('/api/energy/alerts?status=new')
      ]);
      setDashboardData(dashRes.data);
      setMeters(metersRes.data || []);
      setGoals(goalsRes.data || []);
      setCertifications(certsRes.data || []);
      setAlerts(alertsRes.data || []);
    } catch (error) {
      console.error('Error fetching energy data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await api.put(`/api/energy/alerts/${alertId}/acknowledge`);
      toast({ title: 'Alert acknowledged' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const getMeterIcon = (type) => {
    switch (type) {
      case 'electricity': return <Zap className="h-5 w-5 text-yellow-500" />;
      case 'water': return <Droplets className="h-5 w-5 text-blue-500" />;
      case 'gas': return <Flame className="h-5 w-5 text-orange-500" />;
      default: return <Activity className="h-5 w-5 text-gray-500" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'warning';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="energy-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Energy & Sustainability</h1>
          <p className="text-muted-foreground">Monitor consumption and track sustainability goals</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="meters" data-testid="tab-meters">Meters</TabsTrigger>
          <TabsTrigger value="goals" data-testid="tab-goals">Goals</TabsTrigger>
          <TabsTrigger value="certifications" data-testid="tab-certifications">Certifications</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">Alerts</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-6">
          {/* KPI Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Electricity</CardTitle>
                <Zap className="h-4 w-4 text-yellow-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardData?.consumption_summary?.electricity_kwh?.toLocaleString() || 0} kWh
                </div>
                <p className="text-xs text-muted-foreground">Today's consumption</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Water</CardTitle>
                <Droplets className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardData?.consumption_summary?.water_m3?.toLocaleString() || 0} m³
                </div>
                <p className="text-xs text-muted-foreground">Today's consumption</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Gas</CardTitle>
                <Flame className="h-4 w-4 text-orange-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardData?.consumption_summary?.gas_m3?.toLocaleString() || 0} m³
                </div>
                <p className="text-xs text-muted-foreground">Today's consumption</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Carbon Footprint</CardTitle>
                <Leaf className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardData?.consumption_summary?.carbon_kg?.toLocaleString() || 0} kg
                </div>
                <p className="text-xs text-muted-foreground">CO₂ emissions today</p>
              </CardContent>
            </Card>
          </div>

          {/* Summary Cards */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gauge className="h-5 w-5" />
                  Meters Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{dashboardData?.meters?.total || 0}</div>
                <p className="text-sm text-muted-foreground">Active meters</p>
                <div className="mt-4 space-y-2">
                  {dashboardData?.meters?.by_type && Object.entries(dashboardData.meters.by_type).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-sm capitalize">
                        {getMeterIcon(type)} {type}
                      </span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Sustainability Goals
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {dashboardData?.goals?.on_track || 0}/{dashboardData?.goals?.total || 0}
                </div>
                <p className="text-sm text-muted-foreground">Goals on track</p>
                <Progress 
                  value={dashboardData?.goals?.total ? (dashboardData.goals.on_track / dashboardData.goals.total) * 100 : 0} 
                  className="mt-4" 
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  Certifications
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{dashboardData?.certifications?.active || 0}</div>
                <p className="text-sm text-muted-foreground">Active certifications</p>
                <div className="mt-4 space-y-2">
                  {dashboardData?.certifications?.items?.slice(0, 3).map((cert) => (
                    <Badge key={cert.id} variant="outline" className="mr-2">
                      {cert.certification_name}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Alerts Section */}
          {alerts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  Active Alerts ({alerts.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {alerts.slice(0, 5).map((alert) => (
                    <div key={alert.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <Badge variant={getSeverityColor(alert.severity)}>{alert.severity}</Badge>
                        <span className="ml-2 font-medium">{alert.title}</span>
                        <p className="text-sm text-muted-foreground mt-1">{alert.description}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => handleAcknowledgeAlert(alert.id)}>
                        Acknowledge
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="meters" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Energy Meters</h2>
            <Button onClick={() => setShowMeterDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Meter
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Meter</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Last Reading</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {meters.map((meter) => (
                  <TableRow key={meter.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getMeterIcon(meter.meter_type)}
                        <div>
                          <div className="font-medium">{meter.meter_name}</div>
                          <div className="text-sm text-muted-foreground">{meter.meter_code}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{meter.meter_type}</TableCell>
                    <TableCell>{meter.location || '-'}</TableCell>
                    <TableCell>{meter.last_reading?.toLocaleString() || '-'}</TableCell>
                    <TableCell>{meter.unit}</TableCell>
                    <TableCell>
                      <Badge variant={meter.is_active ? 'default' : 'secondary'}>
                        {meter.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {meters.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No meters configured. Add your first meter to start tracking.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="goals" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Sustainability Goals</h2>
            <Button onClick={() => setShowGoalDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Goal
            </Button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {goals.map((goal) => (
              <Card key={goal.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{goal.goal_name}</CardTitle>
                    <Badge variant={goal.status === 'achieved' ? 'default' : goal.status === 'active' ? 'secondary' : 'destructive'}>
                      {goal.status}
                    </Badge>
                  </div>
                  <CardDescription className="capitalize">{goal.goal_type.replace('_', ' ')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between text-sm">
                      <span>Progress</span>
                      <span className="font-medium">{goal.progress_percentage?.toFixed(1) || 0}%</span>
                    </div>
                    <Progress value={goal.progress_percentage || 0} />
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Current: {goal.current_value?.toLocaleString()} {goal.unit}</span>
                      <span>Target: {goal.target_value?.toLocaleString()} {goal.unit}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Target Year: {goal.target_year}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {goals.length === 0 && (
              <Card className="col-span-2">
                <CardContent className="text-center py-8 text-muted-foreground">
                  No sustainability goals set. Create goals to track your progress.
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="certifications" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Green Certifications</h2>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Certification
            </Button>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {certifications.map((cert) => (
              <Card key={cert.id}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <Award className="h-8 w-8 text-green-500" />
                    <div>
                      <CardTitle className="text-lg">{cert.certification_name}</CardTitle>
                      {cert.certification_level && (
                        <CardDescription>{cert.certification_level}</CardDescription>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    {cert.certificate_number && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Certificate #</span>
                        <span>{cert.certificate_number}</span>
                      </div>
                    )}
                    {cert.issued_date && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Issued</span>
                        <span>{new Date(cert.issued_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    {cert.expiry_date && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Expires</span>
                        <span>{new Date(cert.expiry_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    <Badge variant={cert.status === 'active' ? 'default' : 'secondary'} className="mt-2">
                      {cert.status}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
            {certifications.length === 0 && (
              <Card className="col-span-3">
                <CardContent className="text-center py-8 text-muted-foreground">
                  No certifications recorded. Add your green certifications.
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4">
          <h2 className="text-xl font-semibold">Energy Alerts</h2>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Severity</TableHead>
                  <TableHead>Alert</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alerts.map((alert) => (
                  <TableRow key={alert.id}>
                    <TableCell>
                      <Badge variant={getSeverityColor(alert.severity)}>{alert.severity}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{alert.title}</div>
                      <div className="text-sm text-muted-foreground">{alert.description}</div>
                    </TableCell>
                    <TableCell>
                      {alert.value && <span>{alert.value} (threshold: {alert.threshold})</span>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{alert.status}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {new Date(alert.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {alert.status === 'new' && (
                        <Button variant="outline" size="sm" onClick={() => handleAcknowledgeAlert(alert.id)}>
                          Acknowledge
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {alerts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No active alerts. System is operating normally.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
